import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { candidatsService } from '../services/candidats.service';
import { roomsService } from '../services/rooms.service';
import { authService } from '../services/auth.service';
import { RoomDetail } from '../types/room';
import {
  CandidateDetail,
  CandidateStatus,
  getCandidateStatusLabel,
  getCandidateStatusColor,
} from '../types/candidat';
import { formatDate } from '../utils/format';
import '../styles/CandidatsPage.css';
import { getErrorMessage } from '../utils/error';

/** Statuses shown by default in the candidate list filter. Lot 5 hides
 * REJECTED + CONFIRMED behind the dropdown to keep the list focused. */
const DEFAULT_VISIBLE_STATUSES: CandidateStatus[] = ['PENDING', 'ACCEPTED', 'STANDBY'];

/**
 * La LISTE des candidats. Elle ne fait que lister.
 *
 * Le dossier — deux cents lignes de JSX — vit sur sa propre page, et les
 * décisions qu'il porte avec lui : accepter engage un bail, rejeter détruit des
 * pièces, et ni l'un ni l'autre ne se décide sans avoir lu le dossier. C'est le
 * partage déjà retenu pour Agences et pour les dépôts de documents.
 */
const CandidatsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const isAgency = authService.isAgency();
  const detailUrl = (id: string) =>
    isAgency ? `/agency/candidats/${id}` : `/candidats/${id}`;

  const [candidates, setCandidates] = useState<CandidateDetail[]>([]);
  const [rooms, setRooms] = useState<RoomDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters — default to the "active" candidates only (PENDING / ACCEPTED /
  // STANDBY). Owners can switch to a specific status (incl. CONFIRMED /
  // REJECTED) or 'all'.
  const [statusFilter, setStatusFilter] = useState<CandidateStatus | 'all' | 'active'>('active');
  const [roomFilter, setRoomFilter] = useState<string>('all');

  const loadData = useCallback(async () => {
    if (!selectedColoc) {
      setCandidates([]);
      setRooms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isAgency) {
        // L'endpoint chambres est owner-only (l'agence n'accède qu'aux
        // candidatures). On dérive le filtre chambre des candidats eux-mêmes.
        const candidatesData = await candidatsService.listForColoc(selectedColoc.id);
        setCandidates(candidatesData);
        const byRoom = new Map<string, RoomDetail>();
        candidatesData.forEach((c) => {
          if (c.roomId && !byRoom.has(c.roomId)) {
            byRoom.set(c.roomId, { id: c.roomId, name: c.roomName || '' } as unknown as RoomDetail);
          }
        });
        setRooms(Array.from(byRoom.values()));
      } else {
        const [candidatesData, roomsData] = await Promise.all([
          candidatsService.listForColoc(selectedColoc.id),
          roomsService.listForColoc(selectedColoc.id),
        ]);
        setCandidates(candidatesData);
        setRooms(roomsData);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement des candidats'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc, isAgency]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filters — 'active' = PENDING + ACCEPTED + STANDBY (default), 'all' shows
  // everything, or a single status.
  const filteredCandidates = candidates.filter(c => {
    if (statusFilter === 'active') {
      if (!DEFAULT_VISIBLE_STATUSES.includes(c.status)) return false;
    } else if (statusFilter !== 'all' && c.status !== statusFilter) {
      return false;
    }
    if (roomFilter !== 'all' && c.roomId !== roomFilter) return false;
    return true;
  });

  // Unique rooms that have candidates
  const roomsWithCandidates = rooms.filter(r =>
    candidates.some(c => c.roomId === r.id)
  );

  if (!selectedColoc) {
    return <div className="candidats-page"><p>Veuillez sélectionner une colocation.</p></div>;
  }

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <h2>Candidats</h2>
        <div className="candidats-filters">
          <select
            value={roomFilter}
            onChange={e => setRoomFilter(e.target.value)}
            className="candidats-filter-select"
          >
            <option value="all">Toutes les chambres</option>
            {roomsWithCandidates.map(r => (
              <option key={r.id} value={r.id}>{r.name || `Chambre ${r.id.slice(0, 6)}`}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e =>
              setStatusFilter(e.target.value as CandidateStatus | 'all' | 'active')
            }
            className="candidats-filter-select"
          >
            <option value="active">Actifs (par défaut)</option>
            <option value="PENDING">En attente</option>
            <option value="ACCEPTED">Accepté (bail en cours)</option>
            <option value="STANDBY">En réserve</option>
            <option value="CONFIRMED">Locataire (bail signé)</option>
            <option value="REJECTED">Rejeté</option>
            <option value="all">Tous les statuts</option>
          </select>
        </div>
      </div>

      {loading && <div className="candidats-loading">Chargement...</div>}
      {error && <div className="candidats-error">{error}</div>}

      {!loading && !error && filteredCandidates.length === 0 && (
        <div className="socle-vide">
          <p className="socle-vide-titre">Aucun candidat</p>
          <p className="socle-vide-aide">
            Les candidatures déposées depuis le site public arrivent ici. Les
            filtres ci-dessus n'affichent que les dossiers actifs : élargissez-les
            pour retrouver les candidats rejetés ou devenus locataires.
          </p>
        </div>
      )}

      {!loading && !error && filteredCandidates.length > 0 && (
        <div className="candidats-table-container">
        <table className="candidats-table">
          <thead>
            <tr>
              <th>Candidat</th>
              <th>Email</th>
              <th>Chambre</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.map(candidate => (
              <tr
                key={candidate.id}
                className="candidats-row"
                onClick={() => navigate(detailUrl(candidate.id))}
              >
                <td className="candidat-name">
                  <span
                    className="candidat-nom-texte"
                    title={`${candidate.firstName} ${candidate.lastName}`}
                  >
                    {candidate.firstName} {candidate.lastName}
                  </span>
                  {candidate.guarantorType === 'INDIVIDUAL' && (
                    <span className="guarantor-badge">Garant</span>
                  )}
                  {candidate.guarantorType === 'INSTITUTIONAL' && (
                    <span className="guarantor-badge guarantor-badge-scheme">
                      {candidate.guarantorSchemeCode || 'Garantie'}
                    </span>
                  )}
                </td>
                <td>{candidate.email}</td>
                <td>
                  {candidate.roomName || '—'}
                  {isAgency && candidate.roomNumber != null && (
                    <span className="room-number"> (n°{candidate.roomNumber})</span>
                  )}
                </td>
                <td>
                  <span
                    className="candidat-status-badge"
                    style={{ backgroundColor: getCandidateStatusColor(candidate.status) }}
                  >
                    {getCandidateStatusLabel(candidate.status)}
                  </span>
                </td>
                <td>{candidate.dateCreated ? formatDate(candidate.dateCreated) : '—'}</td>
                <td className="candidats-actions" onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-action btn-secondary"
                    onClick={() => navigate(detailUrl(candidate.id))}
                  >
                    Ouvrir le dossier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default CandidatsPage;
